import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesByTransType } from "../../../services/SalesApiServices";
import {
  fetchCustomer,
  fetchProduct,
  fetchSettings,
} from "../../../redux/userSetting";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { getWareHouseList } from "../../../services/MasterApiServices";
import { ReportScreen } from "../../../components/report";
import {
  dateOf,
  dmy,
  groupSum,
  intg,
  money,
  num,
  sumBy,
} from "../../../components/report/reportFormat";

const SalesReturnReport = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { productList, customerList } = useSelector((state) => state.usersetting);

  const [salesData, setsalesData] = useState([]);
  const [wareHouseList, setWareHouseList] = useState([]);

  const handlesalesData = async () => {
    try {
      const response = await getSalesByTransType(branch, 3);
      setsalesData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(
        "Failed to fetch sales return",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  useEffect(() => {
    dispatch(fetchProduct(branch));
    dispatch(fetchCustomer(branch));
    dispatch(fetchSettings(branch));
    dispatch(fetchBranchSettings(branch.guid));
    handlesalesData();
  }, [branch]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWareHouseList();
        setWareHouseList(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  const warehouseMap = useMemo(
    () =>
      Object.fromEntries(
        wareHouseList.map((w) => [w.guid, w.name || "Unnamed Warehouse"])
      ),
    [wareHouseList]
  );

  /* one flat record per return note; the keys match the column definitions */
  const rows = useMemo(
    () =>
      salesData.map((inv) => ({
        guid: inv.guid,
        date: dateOf(inv.invdate),
        billNo: inv.entrynumber || "",
        customer: inv.customer?.name || inv.customerName || "Unknown Customer",
        customerGuid: inv.customer?.guid,
        warehouse: warehouseMap[inv.location?.guid] || inv.location?.name || "-",
        warehouseGuid: inv.location?.guid,
        total: num(inv.grossTotal),
        additional: num(inv.additionalAmt),
        discount: num(inv.discountTotal),
        roundOff: num(inv.roundOff),
        tax: num(inv.taxTotal),
        net: num(inv.netTotal),
        lines: inv.invSalesDetails || [],
      })),
    [salesData, warehouseMap]
  );

  /* ---------------- filters ---------------- */
  const filters = useMemo(
    () => [
      {
        key: "products",
        label: "Products",
        items: (productList || []).map((p) => ({
          v: p.guid,
          t: p.name || "—",
          c: p.code || "",
        })),
        match: (r, v) => r.lines.some((l) => v.includes(l?.product?.guid)),
      },
      {
        key: "customers",
        label: "Customers",
        items: (customerList || []).map((c) => ({ v: c.guid, t: c.name || "—" })),
        match: (r, v) => v.includes(r.customerGuid),
      },
      {
        key: "warehouses",
        label: "Warehouses",
        items: wareHouseList.map((w) => ({
          v: w.guid,
          t: w.name || "Unnamed Warehouse",
        })),
        match: (r, v) => v.includes(r.warehouseGuid),
      },
    ],
    [productList, customerList, wareHouseList]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.customer) ||
      hit(r.warehouse) ||
      hit(r.date) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
    );
  };

  /* ---------------- tabs ---------------- */
  const tabs = useMemo(
    () => [
      {
        id: "detail",
        label: "Detailed report",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.guid,
        groupOptions: [
          { v: "customer", t: "Customer" },
          { v: "warehouse", t: "Warehouse" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "billNo", t: "Bill No", type: "code" },
          { k: "customer", t: "Customer Name", type: "avatar" },
          { k: "warehouse", t: "Warehouse" },
          { k: "total", t: "Total", type: "money" },
          { k: "additional", t: "Additional Amount", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "roundOff", t: "RoundOff", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Total", type: "money" },
        ],
      },
      {
        id: "customer",
        label: "Customer",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(
            list,
            (r) => r.customerGuid || r.customer,
            ["total", "additional", "discount", "roundOff", "tax", "net"],
            (r) => ({ customer: r.customer, last: "" })
          );
          g.forEach((row) => {
            row.last = list
              .filter((r) => (r.customerGuid || r.customer) === row.key)
              .reduce((a, r) => (r.date > a ? r.date : a), "");
            row.avg = row.n ? row.net / row.n : 0;
          });
          return g;
        },
        columns: [
          { k: "customer", t: "Customer Name", type: "avatar" },
          { k: "n", t: "Returns", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "additional", t: "Additional Amount", type: "money", off: true },
          { k: "roundOff", t: "RoundOff", type: "money", off: true },
          { k: "net", t: "Net Total", type: "money" },
          { k: "avg", t: "Avg Return", type: "money", noSum: true },
          { k: "last", t: "Last Return Date", type: "date" },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "item",
        label: "Item-wise",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const m = {};
          list.forEach((r) =>
            r.lines.forEach((l) => {
              const k = l?.product?.guid || l?.product?.name || "unknown";
              if (!m[k])
                m[k] = {
                  key: k,
                  code: l?.product?.code || "—",
                  name: l?.product?.name || "Unknown Item",
                  unit: l?.unit?.name || "",
                  n: 0,
                  qty: 0,
                  total: 0,
                  discount: 0,
                  tax: 0,
                  net: 0,
                };
              const g = m[k];
              const qty = num(l.quantity);
              g.n += 1;
              g.qty += qty;
              g.total += num(l.grossAmount ?? l.amount ?? qty * num(l.price));
              g.discount += num(l.discAmt);
              g.tax += num(l.taxamnt);
              g.net += num(l.netAmt);
            })
          );
          return Object.values(m).map((g) => ({
            ...g,
            avg: g.qty ? g.net / g.qty : 0,
          }));
        },
        columns: [
          { k: "code", t: "Item Code", type: "code" },
          { k: "name", t: "Item Name" },
          { k: "unit", t: "Unit", off: true },
          { k: "n", t: "Lines", type: "int" },
          { k: "qty", t: "Qty", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Total", type: "money" },
          { k: "avg", t: "Avg / Unit", type: "money", noSum: true, off: true },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "daily",
        label: "Day-wise",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(list, (r) => r.date, [
            "total",
            "discount",
            "tax",
            "net",
          ]);
          return g.map((row) => ({
            ...row,
            date: row.key,
            avg: row.n ? row.net / row.n : 0,
          }));
        },
        columns: [
          { k: "date", t: "Date", type: "date" },
          { k: "n", t: "Returns", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Total", type: "money" },
          { k: "avg", t: "Avg Return", type: "money", noSum: true },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
    ],
    []
  );

  /* ---------------- KPIs ---------------- */
  const kpis = useMemo(
    () => [
      { label: "Returns", value: (list) => list.length, format: "int", invert: true },
      { label: "Gross returned", value: (list) => sumBy(list, "total"), invert: true },
      { label: "Discount", value: (list) => sumBy(list, "discount") },
      { label: "VAT reversed", value: (list) => sumBy(list, "tax"), invert: true },
      {
        label: "Net returned",
        value: (list) => sumBy(list, "net"),
        hero: true,
        invert: true,
      },
      {
        label: "Average return",
        value: (list) => (list.length ? sumBy(list, "net") / list.length : 0),
        invert: true,
      },
    ],
    []
  );

  /* ---------------- drill-down ---------------- */
  const drawerContent = (r) => ({
    title: r.customer,
    subtitle: `${r.billNo} · ${dmy(r.date)}`,
    avatarKey: r.customer,
    meta: [
      { k: "Warehouse", v: r.warehouse },
      { k: "Lines", v: intg((r.lines || []).length) },
    ],
    lines: {
      title: "Return lines",
      columns: [
        { t: "Item" },
        { t: "Qty", align: "right" },
        { t: "Rate", align: "right" },
        { t: "Disc", align: "right" },
        { t: "Amount", align: "right" },
      ],
      rows: (r.lines || []).map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">
            {[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}
          </div>
        </>,
        intg(l?.quantity),
        money(l?.price),
        num(l?.discAmt) ? money(l.discAmt) : "—",
        money(l?.netAmt),
      ]),
    },
    totals: [
      { l: "Gross", v: money(r.total) },
      { l: "Discount", v: r.discount ? "−" + money(r.discount) : "0.00" },
      { l: "Additional", v: money(r.additional) },
      { l: "VAT", v: money(r.tax) },
      { l: "Round off", v: money(r.roundOff) },
    ],
    net: { l: "Net total", v: money(r.net) },
  });

  return (
    <ReportScreen
      title="Sales Return Report"
      subtitle="Every sales return raised in the selected period"
      unit="returns"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Sales returns over time" }}
      searchMatch={searchMatch}
      searchPlaceholder="Bill no, customer, item, warehouse…"
      exportName="Sales return"
      reportNames={{
        detail: "Sales Return Detailed Report",
        customer: "Sales Return Customer",
        item: "Sales Return Item Wise",
        daily: "Sales Return Day Wise",
      }}
      onRefresh={handlesalesData}
      drawerContent={drawerContent}
    />
  );
};

export default SalesReturnReport;
